import { TRPCClientError } from "@trpc/client"
import { HttpStatusCodes } from "#/utils/HttpStatusCodes"

// Retrying any of these is either wasteful (4xx will fail again
// identically on the same input) or actively harmful (repeated hits
// against a gateway-timed-out upstream are procyclical and amplify
// the outage we're trying to ride out).
const DO_NOT_RETRY_STATUSES = new Set<number>([
  HttpStatusCodes.BAD_REQUEST,
  HttpStatusCodes.UNAUTHORIZED,
  HttpStatusCodes.FORBIDDEN,
  HttpStatusCodes.NOT_FOUND,
  HttpStatusCodes.SERVICE_UNAVAILABLE,
  HttpStatusCodes.GATEWAY_TIMEOUT
])

// tRPC-aware retry policy for React Query. `buildCarrotApiRetryPolicy`
// reads `error.statusCode`, which is a `ResponseError`-only field —
// TRPCClientError puts the upstream status inside `error.data.httpStatus`,
// so under that policy every tRPC error falls through the bailouts and
// retries `maxRetries` times regardless of status. This one reads the
// right place and honors the "don't retry 504" recommendation.
export function trpcRetry(maxRetries: number) {
  return (failureCount: number, error: unknown): boolean => {
    if (failureCount >= maxRetries) return false

    // Client-side abort or transport failure — no upstream response to
    // reason about, and retrying a torn socket is unlikely to save us.
    if (error instanceof Error && error.name === "AbortError") return false

    if (error instanceof TRPCClientError) {
      const status = (error.data as { httpStatus?: number } | undefined)?.httpStatus
      if (status != null && DO_NOT_RETRY_STATUSES.has(status)) return false
    }

    return true
  }
}
